import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Briefcase,
  Clock,
  Building2,
  ArrowLeft,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  FileText,
  CalendarDays,
  GraduationCap,
  Sparkles,
  IndianRupee,
} from "lucide-react";
import Navbar from "./landing/Navbar";
import Footer from "./landing/Footer";
import { walkinDriveService } from "../services/walkinDriveService";
import { getDriveBySlug } from "../data/walkinDrives";
import { usePrerenderReady } from "../utils/prerender";
import { useSEO } from "../hooks/useSEO";
import {
  buildJobPostingSchema,
  walkinToJobPostingDescriptor,
  buildBreadcrumbList,
  isDriveExpired,
  deriveDriveCity,
} from "../utils/jobPostingSchema";

/* ---------------------------- small bits ---------------------------- */

const SectionTitle = ({ children }) => (
  <h4 className="font-display text-xs font-black uppercase tracking-widest text-ink-300 mb-3">
    {children}
  </h4>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2.5 text-sm text-ink-700 leading-relaxed">
        <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

/* --------------------------- not found ----------------------------- */

function DriveNotFound() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
      <h1 className="font-display text-2xl sm:text-3xl font-black text-ink-900">
        Drive not found
      </h1>
      <p className="mt-3 text-sm text-ink-500">
        This walk-in drive may have closed or the link is incorrect.
      </p>
      <Link
        to="/walkin-drives"
        className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-black tracking-wide
                   text-white bg-brand-saffron hover:brightness-110 active:scale-95 transition-all shadow-saffron"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to all drives
      </Link>
    </div>
  );
}

/* --------------------------- drive closed -------------------------- */

function DriveClosed({ drive, city }) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-black uppercase tracking-wide mb-4">
        <AlertTriangle className="w-3.5 h-3.5" />
        Drive Closed
      </span>
      <h1 className="font-display text-2xl sm:text-3xl font-black text-ink-900">
        {drive.title}
      </h1>
      <p className="mt-3 text-sm text-ink-500">
        This walk-in drive{drive.company ? ` by ${drive.company}` : ""}
        {city ? ` in ${city}` : ""} has closed and is no longer accepting
        candidates.
      </p>
      <Link
        to="/walkin-drives"
        className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-black tracking-wide
                   text-white bg-brand-saffron hover:brightness-110 active:scale-95 transition-all shadow-saffron"
      >
        <ArrowLeft className="w-4 h-4" />
        See live walk-in drives
      </Link>
    </div>
  );
}

/* ----------------------------- page -------------------------------- */

export default function WalkinDriveDetail({ user, isAuthenticated, onLogout }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [drive, setDrive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        // Legacy hard-coded drives take precedence and work without the backend.
        const hard = getDriveBySlug(slug);
        if (hard) {
          if (active) setDrive(hard);
          return;
        }
        const data = await walkinDriveService.get(slug);
        if (active) setDrive(data);
      } catch (e) {
        if (active) setDrive(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  // Signal the prerender crawler once the drive has resolved (found or not).
  usePrerenderReady(!loading);

  const expired = drive ? isDriveExpired(drive) : false;
  const city = drive ? deriveDriveCity(drive) : "";

  // Per-route meta + structured data (Phases 2–4). Recomputed when the drive
  // loads; the prerender layer bakes it into the crawler HTML.
  const seo = useMemo(() => {
    if (!drive) return { title: "Walk-in Drive | JobxBharat" };

    const breadcrumb = buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Walk-in Drives", path: "/walkin-drives" },
      { name: drive.title },
    ]);
    // Never emit an active JobPosting for an expired/closed drive (Phase 4).
    const jobPosting = expired
      ? null
      : buildJobPostingSchema(walkinToJobPostingDescriptor(drive));
    const structuredData = jobPosting ? [jobPosting, breadcrumb] : [breadcrumb];

    const desc = `${drive.company} walk-in drive${city ? ` in ${city}` : ""}. ${
      (drive.overview && drive.overview[0]) || ""
    }`
      .trim()
      .slice(0, 160);

    return {
      title: `${drive.title} — Walk-in Drive${city ? ` in ${city}` : ""} | JobxBharat`,
      description: desc,
      ogTitle: `${drive.title}${city ? ` — ${city}` : ""}`,
      ogDescription: desc,
      ogType: "article",
      // Expired drives should not be indexed as live opportunities.
      ...(expired ? { robots: "noindex, follow" } : {}),
      structuredData,
    };
  }, [drive, expired, city]);

  useSEO(seo);

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Navbar
        onSignIn={() => navigate("/login")}
        onPostJob={() =>
          window.open("https://recruiter.jobxbharat.com/", "_blank")
        }
        user={user}
        isAuthenticated={isAuthenticated}
        onLogout={onLogout}
      />

      <main className="flex-1 pt-[64px] md:pt-[72px]">
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-purple border-t-transparent" />
          </div>
        ) : !drive ? (
          <DriveNotFound />
        ) : expired ? (
          <DriveClosed drive={drive} city={city} />
        ) : (
          <>
            {/* Hero */}
            <header className="bg-gradient-to-br from-brand-purple via-brand-deep to-brand-purple text-white">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                <Link
                  to="/walkin-drives"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-white/70 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  All Walk-in Drives
                </Link>

                <span className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-white text-[11px] font-black uppercase tracking-wide">
                  <Sparkles className="w-3.5 h-3.5" />
                  Walk-in Drive
                </span>

                <h1 className="mt-4 font-display text-2xl sm:text-3xl md:text-4xl font-black leading-tight">
                  {drive.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium text-white/85">
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />
                    {drive.company}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {drive.venue}
                  </span>
                  {drive.meta?.experience && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {drive.meta.experience}
                    </span>
                  )}
                  {drive.salary && (
                    <span className="inline-flex items-center gap-1.5">
                      <IndianRupee className="w-4 h-4" />
                      {drive.salary}
                    </span>
                  )}
                </div>
              </div>
            </header>

            {/* Body */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-7"
            >
              {/* Quick facts grid */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { icon: Briefcase, label: "Role", value: drive.meta?.role },
                  {
                    icon: Building2,
                    label: "Industry",
                    value: drive.meta?.industry,
                  },
                  {
                    icon: FileText,
                    label: "Department",
                    value: drive.meta?.department,
                  },
                  {
                    icon: Clock,
                    label: "Employment Type",
                    value: drive.meta?.employmentType,
                  },
                  {
                    icon: GraduationCap,
                    label: "Education",
                    value: drive.meta?.education,
                  },
                  {
                    icon: Briefcase,
                    label: "Experience",
                    value: drive.meta?.experience,
                  },
                  ...(drive.salary
                    ? [
                        {
                          icon: IndianRupee,
                          label: "Salary",
                          value: drive.salary,
                        },
                      ]
                    : []),
                ]
                  .filter((f) => f.value)
                  .map((f) => (
                  <div
                    key={f.label}
                    className="rounded-xl bg-white border border-gray-100 p-3"
                  >
                    <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-ink-300">
                      <f.icon className="w-3.5 h-3.5" />
                      {f.label}
                    </div>
                    <p className="mt-1 text-sm font-semibold text-ink-900 leading-snug">
                      {f.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Overview */}
              {drive.overview?.length > 0 && (
                <div>
                  <SectionTitle>Job Description</SectionTitle>
                  <div className="space-y-2.5">
                    {drive.overview.map((p, i) => (
                      <p key={i} className="text-sm text-ink-700 leading-relaxed">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Time & Venue — highlighted */}
              {drive.timeAndVenue && (
                <div className="rounded-xl border border-brand-purple/25 bg-brand-purple/5 p-4">
                  <div className="flex items-center gap-2 text-brand-purple font-black text-xs uppercase tracking-wide mb-3">
                    <CalendarDays className="w-4 h-4" />
                    Time & Venue
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex gap-2.5 text-sm text-ink-700">
                      <CalendarDays className="w-4 h-4 text-brand-purple shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-ink-900">
                          {drive.timeAndVenue.dates}
                        </span>
                        {drive.timeAndVenue.time && (
                          <>
                            {" · "}
                            {drive.timeAndVenue.time}
                          </>
                        )}
                      </span>
                    </div>
                    <div className="flex gap-2.5 text-sm text-ink-700">
                      <MapPin className="w-4 h-4 text-brand-purple shrink-0 mt-0.5" />
                      <span>
                        {drive.timeAndVenue.place}
                        {drive.mapUrl && (
                          <a
                            href={drive.mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1.5 inline-flex items-center gap-1 font-bold text-brand-saffron hover:underline"
                          >
                            (View on map)
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </span>
                    </div>
                    {drive.contactPerson && (
                      <div className="flex gap-2.5 text-sm text-ink-700">
                        <FileText className="w-4 h-4 text-brand-purple shrink-0 mt-0.5" />
                        <span>
                          Contact —{" "}
                          <span className="font-bold text-ink-900">
                            {drive.contactPerson}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Roles open */}
              {drive.rolesOpen?.length > 0 && (
                <div>
                  <SectionTitle>Roles Open</SectionTitle>
                  <div className="flex flex-wrap gap-2">
                    {drive.rolesOpen.map((r) => (
                      <span
                        key={r}
                        className="px-3 py-1.5 rounded-lg bg-brand-purple/10 text-brand-purple text-xs font-bold"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Responsibilities */}
              {drive.responsibilities?.length > 0 && (
                <div>
                  <SectionTitle>Key Responsibilities</SectionTitle>
                  <div className="space-y-5">
                    {drive.responsibilities.map((group) => (
                      <div key={group.title}>
                        <p className="text-sm font-bold text-ink-900 mb-2.5">
                          {group.title}
                        </p>
                        <BulletList items={group.items || []} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Requirements */}
              {drive.requirements?.length > 0 && (
                <div>
                  <SectionTitle>Requirements</SectionTitle>
                  <BulletList items={drive.requirements} />
                </div>
              )}

              {/* Perks */}
              {drive.perks?.length > 0 && (
                <div>
                  <SectionTitle>Perks & Benefits</SectionTitle>
                  <BulletList items={drive.perks} />
                </div>
              )}

              {/* Walk-in details — highlighted */}
              {(drive.walkInDetails || drive.contactPerson) && (
                <div className="rounded-xl border border-brand-green/30 bg-brand-green/5 p-4">
                  <div className="flex items-center gap-2 text-brand-green font-black text-xs uppercase tracking-wide mb-2">
                    <FileText className="w-4 h-4" />
                    Walk-in Details
                  </div>
                  {drive.walkInDetails && (
                    <p className="text-sm text-ink-700 leading-relaxed">
                      {drive.walkInDetails}
                    </p>
                  )}
                  {drive.contactPerson && (
                    <p className="mt-2 text-sm text-ink-700">
                      Mention on resume:{" "}
                      <span className="font-bold text-ink-900">
                        {drive.contactPerson}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {/* Important note — amber */}
              {drive.importantNote && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 leading-relaxed font-medium">
                    {drive.importantNote}
                  </p>
                </div>
              )}

              {/* Key skills */}
              {drive.keySkills?.length > 0 && (
                <div>
                  <SectionTitle>Key Skills</SectionTitle>
                  <div className="flex flex-wrap gap-2">
                    {drive.keySkills.map((s) => (
                      <span
                        key={s}
                        className="px-3 py-1.5 rounded-full bg-gray-100 text-ink-700 text-xs font-semibold"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* About company */}
              {drive.about && (
                <div>
                  <SectionTitle>About Company</SectionTitle>
                  <p className="text-sm text-ink-500 leading-relaxed">
                    {drive.about}
                  </p>
                </div>
              )}

              {/* CTA — map / directions / listing */}
              {(drive.mapUrl || drive.naukriUrl) && (
                <div className="pt-1">
                  <a
                    href={drive.mapUrl || drive.naukriUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-black tracking-wide
                               text-white bg-brand-saffron hover:brightness-110 active:scale-95 transition-all shadow-saffron"
                  >
                    <MapPin className="w-4 h-4" />
                    {drive.mapUrl ? "View Venue on Map" : "View Full Listing"}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
            </motion.div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
